import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, Trash, ArrowRight, ShieldCheck, Bank } from "@phosphor-icons/react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";

export default function Cart() {
  const { items, remove, setQty, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { fmt, currency } = useCurrency();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [ship, setShip] = useState({
    name: user?.name || "", email: user?.email || "", phone: "", address: "", city: "", postcode: "", country: "United Kingdom",
  });
  const set = (k) => (e) => setShip({ ...ship, [k]: e.target.value });

  const checkout = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        shipping: ship,
        currency,
      });
      clear();
      navigate(`/checkout/success?order=${data.order.order_number}`, { state: { order: data.order } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Order failed");
      setBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <>
        <SEO title="Your Cart" description="Review your electric bike order from our Kent e-bike shop." path="/cart" />
        <div className="container-px py-28 text-center">
          <ShoppingCart size={64} className="mx-auto text-muted-foreground/40" />
          <h1 className="mt-6 text-3xl font-black">Your cart is empty</h1>
          <p className="mt-2 text-muted-foreground">Looks like you haven't added any bikes yet.</p>
          <Link to="/shop" className="btn-primary mt-7">Browse eBikes <ArrowRight size={18} /></Link>
        </div>
      </>
    );
  }

  const total = subtotal;

  return (
    <>
      <SEO title="Checkout" description="Complete your electric bike order — free delivery, taxes included, pay in GBP, EUR or USD." path="/cart" />
      <div className="container-px py-12">
        <h1 className="text-4xl font-black tracking-tight">Checkout</h1>
        <form onSubmit={checkout} className="mt-10 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          {/* Left: items + customer details */}
          <div className="space-y-8">
            <div className="rounded-3xl border border-border bg-card p-5 sm:p-6">
              <h2 className="text-lg font-bold">Your items ({items.length})</h2>
              <div className="mt-4 space-y-4">
                {items.map((i) => (
                  <div key={i.slug} className="flex gap-4 rounded-2xl border border-border p-3" data-testid={`cart-row-${i.slug}`}>
                    <img src={i.thumbnail} alt={i.name} className="h-24 w-24 rounded-xl object-cover" loading="lazy" />
                    <div className="flex flex-1 flex-col">
                      <Link to={`/product/${i.slug}`} className="font-bold hover:text-primary">{i.name}</Link>
                      <span className="text-sm text-muted-foreground">{fmt(i.price)} each</span>
                      <div className="mt-auto flex items-center justify-between">
                        <div className="flex items-center gap-2 rounded-full border border-border">
                          <button type="button" onClick={() => setQty(i.slug, i.quantity - 1)} className="grid h-7 w-7 place-items-center rounded-full hover:bg-muted" aria-label="Decrease"><Minus size={13} /></button>
                          <span className="w-5 text-center text-sm font-semibold">{i.quantity}</span>
                          <button type="button" onClick={() => setQty(i.slug, i.quantity + 1)} className="grid h-7 w-7 place-items-center rounded-full hover:bg-muted" aria-label="Increase"><Plus size={13} /></button>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-extrabold">{fmt(i.price * i.quantity)}</span>
                          <button type="button" onClick={() => remove(i.slug)} className="text-muted-foreground hover:text-primary" aria-label="Remove"><Trash size={17} /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-5 sm:p-6">
              <h2 className="text-lg font-bold">Your details</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <input required placeholder="Full name *" value={ship.name} onChange={set("name")} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-name" />
                <input required type="email" placeholder="Email address *" value={ship.email} onChange={set("email")} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-email" />
                <input required placeholder="Address *" value={ship.address} onChange={set("address")} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-address" />
                <input required placeholder="ZIP / Postal code *" value={ship.postcode} onChange={set("postcode")} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-postcode" />
                <input required placeholder="City *" value={ship.city} onChange={set("city")} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-city" />
                <input required placeholder="Country *" value={ship.country} onChange={set("country")} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-country" />
                <input placeholder="Phone (optional)" value={ship.phone} onChange={set("phone")} className="sm:col-span-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="ship-phone" />
              </div>
            </div>
          </div>

          {/* Right: summary */}
          <div className="h-fit lg:sticky lg:top-24">
            <div className="rounded-3xl border border-border bg-card p-6">
              <h2 className="text-lg font-bold">Order summary</h2>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-semibold" data-testid="summary-subtotal">{fmt(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="font-semibold text-green-600" data-testid="summary-shipping">Free</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Taxes (VAT)</span><span className="font-semibold" data-testid="summary-taxes">Included</span></div>
                <div className="flex justify-between border-t border-border pt-3 text-lg font-black"><span>Grand Total</span><span data-testid="summary-total">{fmt(total)}</span></div>
                <div className="flex items-start gap-2 rounded-xl bg-muted px-3 py-2.5 text-xs text-muted-foreground" data-testid="bank-transfer-note">
                  <Bank size={15} className="mt-0.5 shrink-0" /> Payment by Direct Bank Transfer — after you place your order, our team will email you everything you need to complete payment.
                </div>
              </div>
              <button disabled={busy} className="btn-primary mt-5 w-full" data-testid="proceed-payment">
                {busy ? "Placing order…" : <><Bank size={18} /> Place Order</>}
              </button>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-muted-foreground"><ShieldCheck size={14} /> No card needed · Your bike is reserved for 48 hours</p>
              <button type="button" onClick={clear} className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-primary">Clear cart</button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
